import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, Switch, Grid, TextField, Typography, Divider, Link as MuiLink, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import StatusField from './StatusField';
import { canEditField } from '../hartUtils';

// INITIAL FORM DATA - Default values for creating a new job
export const jobInitialData = {
  job_no: '',
  job_name: '',
  start_date: null,
  end_date: null,
  close_date: null,
  job_category: null,
  status: 'Active',  // Changed to capitalized format to match new JobStatus model
  job_type: [],
  analysts: [],
  proposal_link: '',
  lessons_learned: '',
  has_oversample: false,
  abstract: '',
  release_status: '',
  firewall_status: '',
  firewall_expiration: null,
};

// FORM VALIDATION
export function validateJob(data) {
  const errors = {};

  // Job No is required
  if (!data.job_no || !data.job_no.trim()) {
    errors.job_no = 'Job No is required';
  }

  // Job Name is required
  if (!data.job_name || !data.job_name.trim()) {
    errors.job_name = 'Job Name is required';
  }

  // Validate URL format for proposal_link if provided
  if (data.proposal_link && data.proposal_link.trim()) {
    try {
      new URL(data.proposal_link);
    } catch (e) {
      errors.proposal_link = 'Please enter a valid URL (e.g., https://example.com)';
    }
  }

  // Validate date logic - end date should be after start date
  if (data.start_date && data.end_date) {
    const startDate = new Date(data.start_date);
    const endDate = new Date(data.end_date);
    if (endDate < startDate) {
      errors.end_date = 'End date must be after start date';
    }
  }

  return errors;
}

// NULLABLE FIELDS - Fields that should be converted from empty string to null
export const jobNullableFields = [
  'job_category',
  'start_date',
  'end_date',
  'close_date',
  'job_name',
  'proposal_link',
  'lessons_learned',
  'abstract',
  'release_status',
  'firewall_status',
  'firewall_expiration'
];

// Helper function to format dates for display (view mode)
const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    // Plain date string (YYYY-MM-DD)
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    // Fallback for other formats
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

// Helper function to format dates for input fields (edit mode)
// Return YYYY-MM-DD format for date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return dateString;
  }
};

// Simple FormField that handles both view and edit modes
const FormField = ({ label, value, editable, onChange, error, helperText, required, type, multiline, minRows, maxRows, isLink, formatValue }) => {
  if (!editable) {
    // View mode
    const displayValue = formatValue ? formatValue(value) : (value || '—');
    
    if (multiline) {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {displayValue && displayValue !== '—' && String(displayValue).trim() ? displayValue : '—'}
          </Typography>
        </>
      );
    }
    
    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        {isLink && displayValue !== '—' ? (
          <MuiLink 
            href={displayValue} 
            target="_blank" 
            rel="noopener noreferrer" 
            underline="always"
            sx={{ 
              wordBreak: 'break-all', 
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        ) : (
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {displayValue}
          </Typography>
        )}
      </Box>
    );
  }
  
  // Edit mode - format date values for input fields
  const inputValue = type === 'date' ? formatDateForInput(value) : (value || '');
  
  return (
    <TextField
      label={label}
      value={inputValue}
      fullWidth
      required={required}
      type={type}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      onChange={onChange}
      variant="outlined"
      error={!!error}
      helperText={error || helperText || ''}
      InputLabelProps={{
        shrink: type === 'date' ? true : undefined,
        sx: { color: 'primary.main', fontWeight: 600 }
      }}
    />
  );
};

const JobFormFields = ({ data, editable, onChange, errors = {}, displayName, projectId, isCreating = false }) => {
  const gridSpacing = editable ? 3 : 4;
  const gridBreakpoint = editable ? 'sm' : 'md';

  const fieldEditable = (fieldName) => {
    return editable && canEditField(fieldName, 'job', projectId);
  };
  
  // State for dropdown options
  const [jobCategories, setJobCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [releaseStatuses, setReleaseStatuses] = useState([]);
  
  // Fetch dropdown values on mount
  useEffect(() => {
    const fetchDropdownValues = async () => {
      try {
        // Fetch job categories
        const categoriesResponse = await fetch('/hart/job_categories', {
          credentials: 'include',
        });
        if (categoriesResponse.ok) {
          const categories = await categoriesResponse.json();
          setJobCategories(categories || []);
        }
        
        // Fetch job types
        const typesResponse = await fetch('/hart/job_types', {
          credentials: 'include',
        });
        if (typesResponse.ok) {
          const types = await typesResponse.json();
          setJobTypes(types || []);
        }
        
        // Fetch release statuses
        const releaseResponse = await fetch('/hart/release_statuses', {
          credentials: 'include',
        });
        if (releaseResponse.ok) {
          const statuses = await releaseResponse.json();
          setReleaseStatuses(statuses || []);
        }
      } catch (error) {
        console.error('Error fetching dropdown values:', error);
      }
    };
    
    fetchDropdownValues();
  }, []);
  
  // Helper to find matching category (case-insensitive)
  const findMatchingCategory = (value) => {
    if (!value) return '';
    const match = jobCategories.find(
      cat => cat.name.toLowerCase() === value.toLowerCase()
    );
    return match ? match.name : '';
  };
  
  // Helper to find matching types (case-insensitive)
  const findMatchingTypes = (values) => {
    if (!values || !Array.isArray(values)) return [];
    return values
      .map(val => {
        const match = jobTypes.find(
          type => type.name.toLowerCase() === val.toLowerCase()
        );
        return match ? match.name : null;
      })
      .filter(Boolean);
  };
  
  // Helper to find matching release status
  const findMatchingReleaseStatus = (value) => {
    if (!value) return '';
    // Handle if value is an object (ForeignKey)
    if (typeof value === 'object' && value.name) {
      return value.name;
    }
    const match = releaseStatuses.find(
      status => status.name.toLowerCase() === String(value).toLowerCase()
    );
    return match ? match.name : '';
  };
  
  return (
    <Box component={editable ? "form" : "div"} sx={{ mt: 0, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 0 }}>
          {displayName} Details
        </Typography>
        <StatusField 
          data={data} 
          editable={fieldEditable('status')} 
          onChange={onChange}
        />
      </Box>

      <Grid container spacing={gridSpacing}>
        {/* Row 1: Job No | Job Name */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Job No"
            value={data.job_no}
            editable={isCreating && fieldEditable('job_no')}
            onChange={(e) => onChange({ ...data, job_no: e.target.value })}
            error={errors.job_no}
            required
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Job Name"
            value={data.job_name}
            editable={fieldEditable('job_name')}
            onChange={(e) => onChange({ ...data, job_name: e.target.value })}
            error={errors.job_name}
            required
          />
        </Grid>

        {/* Row 2: Field Start Date | Field End Date */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Field Start Date"
            value={data.start_date}
            editable={fieldEditable('start_date')}
            onChange={(e) => onChange({ ...data, start_date: e.target.value })}
            type="date"
            formatValue={formatDate}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Field End Date"
            value={data.end_date}
            editable={fieldEditable('end_date')}
            onChange={(e) => onChange({ ...data, end_date: e.target.value })}
            type="date"
            error={errors.end_date}
            formatValue={formatDate}
          />
        </Grid>

        {/* Row 3: Create Date | Close Date */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Create Date"
            value={data.created}
            editable={false}
            formatValue={formatDate}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Close Date"
            value={data.close_date}
            editable={fieldEditable('close_date')}
            onChange={(e) => onChange({ ...data, close_date: e.target.value })}
            type="date"
            formatValue={formatDate}
          />
        </Grid>

        {/* Row 3: Oversample (full width) */}
        <Grid item xs={12}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
              Has OverSample
            </Typography>
            {fieldEditable('has_oversample') && editable ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(data.has_oversample)}
                    onChange={(e) => onChange({ ...data, has_oversample: e.target.checked })}
                    color="primary"
                  />
                }
                label={data.has_oversample ? 'True' : 'False'}
                sx={{ ml: 0, mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ color: 'text.primary' }}>
                {data.has_oversample === true ? 'True' : data.has_oversample === false ? 'False' : '—'}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Row 4: Abstract (full width) */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
          <FormField
            label="Abstract"
            value={data.abstract}
            editable={fieldEditable('abstract')}
            onChange={(e) => onChange({ ...data, abstract: e.target.value })}
            multiline
            minRows={4}
            maxRows={20}
          />
        </Grid>

        {/* Row 5: Firewall Status | Firewall Expiration Date */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Firewall Status"
            value={data.firewall_status}
            editable={false}
            onChange={(e) => onChange({ ...data, firewall_status: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Firewall Expiration Date"
            value={data.firewall_expiration}
            editable={false}
            onChange={(e) => onChange({ ...data, firewall_expiration: e.target.value })}
            type="date"
            formatValue={formatDate}
          />
        </Grid>

        {/* Row 6: Job Type | Job Category */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          {fieldEditable('job_type') ? (
            <FormControl fullWidth error={!!errors.job_type}>
              <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Job Type</InputLabel>
              <Select
                multiple
                value={findMatchingTypes(data.job_type)}
                onChange={(e) => onChange({ ...data, job_type: e.target.value })}
                label="Job Type"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {jobTypes.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.job_type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.job_type}
                </Typography>
              )}
            </FormControl>
          ) : (
            <FormField
              label="Job Type"
              value={Array.isArray(data.job_type) ? data.job_type.join(', ') : (data.job_type || '')}
              editable={false}
              formatValue={(val) => Array.isArray(val) ? val.join(', ') : val}
            />
          )}
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          {fieldEditable('job_category') ? (
            <FormControl fullWidth error={!!errors.job_category}>
              <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Job Category</InputLabel>
              <Select
                value={findMatchingCategory(data.job_category) || ''}
                onChange={(e) => onChange({ ...data, job_category: e.target.value })}
                label="Job Category"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {jobCategories.map((cat) => (
                  <MenuItem key={cat.name} value={cat.name}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.job_category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.job_category}
                </Typography>
              )}
            </FormControl>
          ) : (
            <FormField
              label="Job Category"
              value={data.job_category}
              editable={false}
            />
          )}
        </Grid>

        {/* Row 7: Release Status | Proposal Link */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          {fieldEditable('release_status') ? (
            <FormControl fullWidth error={!!errors.release_status}>
              <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Release Status</InputLabel>
              <Select
                value={findMatchingReleaseStatus(data.release_status) || ''}
                onChange={(e) => onChange({ ...data, release_status: e.target.value })}
                label="Release Status"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {releaseStatuses.map((status) => (
                  <MenuItem key={status.name} value={status.name}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.release_status && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.release_status}
                </Typography>
              )}
            </FormControl>
          ) : (
            <FormField
              label="Release Status"
              value={typeof data.release_status === 'object' && data.release_status?.name ? data.release_status.name : data.release_status}
              editable={false}
            />
          )}
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Proposal Link"
            value={data.proposal_link}
            editable={fieldEditable('proposal_link')}
            onChange={(e) => onChange({ ...data, proposal_link: e.target.value })}
            type="url"
            error={errors.proposal_link}
            helperText="Enter a valid URL (e.g., https://example.com)"
            isLink
          />
        </Grid>

        {/* Row 8: Lessons Learned (full width) */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
          <FormField
            label="Lessons Learned"
            value={data.lessons_learned}
            editable={fieldEditable('lessons_learned')}
            onChange={(e) => onChange({ ...data, lessons_learned: e.target.value })}
            multiline
            minRows={4}
            maxRows={20}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

JobFormFields.propTypes = {
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  displayName: PropTypes.string,
  projectId: PropTypes.string,
  isCreating: PropTypes.bool,
};

export default JobFormFields;
